/**
 * Date utility functions for safe date handling across the application
 */

/**
 * Ensures that a value is converted to a valid Date object.
 * Handles Date objects, strings, numbers (timestamps), and invalid values.
 *
 * @param value - The value to convert to a Date
 * @param fallback - Optional fallback date if conversion fails (defaults to current date)
 * @returns A valid Date object
 *
 * @example
 * ensureDate('2024-01-01') // Date object for Jan 1, 2024
 * ensureDate(1704067200000) // Date object from timestamp
 * ensureDate(null) // Current date
 * ensureDate(undefined, new Date(2024, 0, 1)) // Fallback date
 */
export function ensureDate(value: any, fallback?: Date): Date {
  // If no value provided, use fallback or current date
  if (!value) {
    return fallback || new Date()
  }

  // If already a Date instance, return it
  if (value instanceof Date) {
    // Check if it's a valid date
    return Number.isNaN(value.getTime()) ? fallback || new Date() : value
  }

  // Try to convert to Date
  const date = new Date(value)

  // If conversion failed, use fallback or current date
  return Number.isNaN(date.getTime()) ? fallback || new Date() : date
}

/**
 * True when the value is a Date holding a real (non-NaN) time.
 * NOTE: an Invalid Date instance is TRUTHY — `value ? …` checks do not catch
 * it, and feeding one to ensureDate() without a fallback silently substitutes
 * a fresh `new Date()` on EVERY call. Anything deriving react-query keys from
 * dates must validate with this first (2026-07-09 /api/emrs request loop).
 */
export function isValidDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime())
}

/**
 * Converts a value to a Unix timestamp in seconds.
 *
 * @param value - Date value (Date object, string, or number)
 * @returns Unix timestamp in seconds
 */
export function toUnixTimestamp(value: any): number {
  const date = ensureDate(value)
  return Math.floor(date.getTime() / 1000)
}

/**
 * Converts a Unix timestamp (in seconds) to a Date object.
 *
 * @param timestamp - Unix timestamp in seconds
 * @returns Date object
 */
export function fromUnixTimestamp(timestamp: number): Date {
  return new Date(timestamp * 1000)
}

/**
 * Checks if two dates represent the same day (ignoring time).
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns True if dates are on the same day
 */
export function isSameDay(date1: any, date2: any): boolean {
  const d1 = ensureDate(date1)
  const d2 = ensureDate(date2)

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  )
}

/**
 * Formats a date range as a human-readable string.
 *
 * @param startDate - Start date of range
 * @param endDate - End date of range
 * @returns Formatted date range string
 */
export function formatDateRange(startDate: any, endDate: any): string {
  const start = ensureDate(startDate)
  const end = ensureDate(endDate)

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }

  return `${start.toLocaleDateString(undefined, options)} - ${end.toLocaleDateString(undefined, options)}`
}
